import { useEffect, useState } from 'react';
import { useMachineStore } from '@/store/store';
import { DEMO_MODE } from '@/types/machine.types';
import { useMachineConnect } from '@/hooks/useMachineConnect';
import { usePhoneBLEConnection } from '@/hooks/usePhoneBLEConnection';
import { useAutoUpdater } from '@/hooks/useAutoUpdater';
import { SafetyOverlay } from '@/components/SafetyOverlay/SafetyOverlay';
import { StatusHeader } from '@/components/StatusHeader/StatusHeader';
import { NavBar, type Tab } from '@/components/NavBar/NavBar';
import { HomePage } from '@/components/HomePage/HomePage';
import { ToolsPage } from '@/components/ToolsPage/ToolsPage';
import { ProbePage } from '@/components/ProbePage/ProbePage';
import { AlarmBanner } from '@/components/AlarmBanner/AlarmBanner';
import { ToastNotifications } from '@/components/ToastNotifications/ToastNotifications';

const PAGES: Record<Tab, () => JSX.Element> = {
  home: HomePage,
  tools: ToolsPage,
  probe: ProbePage,
};

export function App(): JSX.Element {
  useMachineConnect();
  usePhoneBLEConnection();
  useAutoUpdater();

  // Load the tool geometry cache at startup so the active tool's spec shows
  // everywhere (Home badge, preview) before the Tool tab is ever opened. On a
  // cold boot the backend HTTP server may not be up yet, so retry until the
  // first successful fetch, then stop.
  useEffect(() => {
    // Web/demo build has no backend — skip the loopback HTTP poll entirely so
    // the browser never prompts for local-network access.
    if (DEMO_MODE) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const attempt = (): void => {
      void useMachineStore
        .getState()
        .refreshToolCatalog()
        .then((ok) => {
          if (!ok && !cancelled) timer = setTimeout(attempt, 2000);
        });
    };
    attempt();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  const [activeTab, setActiveTab] = useState<Tab>('home');
  const ActivePage = PAGES[activeTab];

  return (
    <div className="app">
      <SafetyOverlay />
      <AlarmBanner />
      <ToastNotifications />

      <div className="app_main">
        <StatusHeader />

        <main className="app_content">
          <NavBar active={activeTab} onChange={setActiveTab} />
          <ActivePage />
        </main>
      </div>
    </div>
  );
}
